import React from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowRight, Calendar, MapPin } from "lucide-react";
import AnimatedBackground from "../components/AnimatedBackground";
import { TOURNAMENT_INFO, ASSETS } from "../data/tournament";

const HERO_BG = ASSETS.team_ice;
const TOURNAMENT_INFO_LOGO = ASSETS.logo;

const Splash = () => {
  return (
    <div className="relative w-full h-screen overflow-hidden bg-black" data-testid="splash-page">
      <AnimatedBackground image={HERO_BG} intensity="high" />

      {/* Top brand strip */}
      <motion.div
        initial={{ opacity: 0, y: -20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.8 }}
        className="absolute top-0 left-0 right-0 z-20 px-6 lg:px-12 py-6 flex items-center justify-between"
      >
        <div className="flex items-center gap-3">
          <img src={TOURNAMENT_INFO_LOGO} alt="Coupe CH" className="h-12 w-12 object-contain" />
          <span className="font-condensed uppercase tracking-[0.3em] text-xs text-white/70">
            Coupe CH · 2026
          </span>
        </div>
        <div className="hidden sm:flex items-center gap-2 text-white/50 text-xs uppercase tracking-[0.3em] font-condensed">
          <span className="w-2 h-2 bg-[#E60012] rounded-full animate-pulse" />
          Édition officielle
        </div>
      </motion.div>

      {/* Center hero */}
      <div className="relative z-10 h-full flex flex-col items-center justify-center px-6 text-center">
        {/* Pre-headline */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.3, duration: 0.7 }}
          className="flex items-center gap-3 mb-6"
        >
          <span className="h-px w-10 bg-[#E60012]" />
          <span className="font-condensed uppercase tracking-[0.4em] text-xs text-white/80">
            Tournoi de Hockey · Jeunes
          </span>
          <span className="h-px w-10 bg-[#E60012]" />
        </motion.div>

        {/* Big logo replacing the COUPE CH text */}
        <motion.div
          initial={{ opacity: 0, scale: 0.85, y: 30 }}
          animate={{ opacity: 1, scale: 1, y: 0 }}
          transition={{ delay: 0.5, duration: 1, ease: [0.16, 1, 0.3, 1] }}
          className="flex items-center justify-center"
          data-testid="splash-title"
        >
          <img
            src={ASSETS.logo_transparent}
            alt="Coupe CH"
            className="w-[68vw] sm:w-[50vw] lg:w-[40vw] xl:w-[34vw] max-w-[640px] h-auto object-contain"
            style={{
              filter: "drop-shadow(0 0 80px rgba(230,0,18,0.5)) drop-shadow(0 8px 30px rgba(0,0,0,0.7))",
            }}
            draggable="false"
          />
        </motion.div>

        {/* Slash line */}
        <motion.div
          initial={{ scaleX: 0 }}
          animate={{ scaleX: 1 }}
          transition={{ delay: 1.0, duration: 0.7 }}
          className="h-[3px] w-32 lg:w-56 bg-[#E60012] my-3 origin-left"
        />

        {/* Dates + venues */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 1.2, duration: 0.7 }}
          className="flex flex-col sm:flex-row items-center gap-4 sm:gap-8 text-white/85 mb-10"
        >
          <div className="flex items-center gap-2 font-condensed uppercase tracking-[0.2em] text-sm">
            <Calendar className="w-4 h-4 text-[#E60012]" />
            {TOURNAMENT_INFO.dates}
          </div>
          <span className="hidden sm:block w-1 h-1 bg-white/40 rounded-full" />
          <div className="flex items-center gap-2 font-condensed uppercase tracking-[0.2em] text-sm">
            <MapPin className="w-4 h-4 text-[#E60012]" />
            CCSSJ · Sainte-Julie
          </div>
        </motion.div>

        {/* CTA */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 1.4, duration: 0.7 }}
        >
          <Link
            to="/accueil"
            data-testid="acceder-site-btn"
            className="group relative inline-flex items-center gap-4 pl-8 pr-3 py-3 bg-[#E60012] hover:bg-white text-white hover:text-black transition-all duration-300 btn-pulse"
            style={{ borderRadius: "9999px" }}
          >
            <span className="font-condensed uppercase tracking-[0.25em] text-sm font-semibold">
              Accéder au site
            </span>
            <span className="w-10 h-10 rounded-full bg-white text-[#E60012] flex items-center justify-center group-hover:bg-[#E60012] group-hover:text-white transition-colors">
              <ArrowRight className="w-5 h-5 group-hover:translate-x-0.5 transition-transform" />
            </span>
          </Link>
        </motion.div>

        {/* Tagline bottom */}
        <motion.p
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 1.8, duration: 1 }}
          className="absolute bottom-10 left-0 right-0 text-center font-condensed uppercase tracking-[0.5em] text-xs text-white/50"
          data-testid="splash-tagline"
        >
          UN TOURNOI · UNE PASSION · DES SOUVENIRS POUR LA VIE
        </motion.p>
      </div>

      {/* Side ribbons */}
      <div className="hidden lg:block absolute left-0 top-1/2 -translate-y-1/2 -rotate-90 origin-left z-10">
        <span className="font-condensed uppercase tracking-[0.5em] text-xs text-white/30 pl-8">
          1 · 2 · 3 Mai 2026
        </span>
      </div>
      <div className="hidden lg:block absolute right-0 top-1/2 -translate-y-1/2 rotate-90 origin-right z-10">
        <span className="font-condensed uppercase tracking-[0.5em] text-xs text-white/30 pr-8">
          M9 · 2017 / 2018
        </span>
      </div>
    </div>
  );
};

export default Splash;
